import React from 'react';
import FormElements, {
  FORMELEMENTS_CHECKBOXES,
  FORMELEMENTS_DATEPICKER,
  FORMELEMENTS_DROPDOWN,
  FORMELEMENTS_NUMBERINPUT,
  FORMELEMENTS_RADIOBUTTONS,
  FORMELEMENTS_TEXTAREA,
  FORMELEMENTS_TEXTINPUT
} from './FormElements';
import {
  defineLayoutSize,
  formWidgetHtmlId,
  getAbsPlaceStyle,
  getAbsolutePlaceTooltipInfo,
  prepareDateToFormData,
  toggleBoxesValue
} from './utils';
import PropTypes from 'prop-types';
import { shallowEqual } from '../helpers/utils';
import {
  getFormItemErrorMessage,
  isBoxesWidget,
  isOptionsWidget
} from '../helpers/formUtils';
import { md5Password } from '../home/utils';

const DEBOUNCED_ELEMENTS = new Set([
  FORMELEMENTS_TEXTINPUT,
  FORMELEMENTS_NUMBERINPUT,
  FORMELEMENTS_TEXTAREA
]);

const CHAR_COUNTDOWN_ELEMENTS = new Set([
  FORMELEMENTS_TEXTINPUT,
  FORMELEMENTS_NUMBERINPUT,
  FORMELEMENTS_TEXTAREA
]);

const TEXT_LIKE_ELEMENTS = new Set([
  FORMELEMENTS_TEXTINPUT,
  FORMELEMENTS_NUMBERINPUT,
  FORMELEMENTS_TEXTAREA
]);

const FORM_INPUT_DEBOUNCE_MS = {
  desktop: 300,
  mobile: 300
};

function isEmptyFormValue(value) {
  return value === undefined || value === null || value === '';
}

function resolveRecipientHint(item) {
  return (item.tooltip && String(item.tooltip).trim()) || '';
}

function resolveMaxLength(maxLength) {
  const parsed = parseInt(maxLength, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function getCharCountdownInfo(item, value, readOnlyMode) {
  if (readOnlyMode || !CHAR_COUNTDOWN_ELEMENTS.has(item.element)) {
    return null;
  }

  const maxLength = resolveMaxLength(item.maxLength);
  if (!maxLength) {
    return null;
  }

  const used = String(value == null ? '' : value).length;
  return `${used}/${maxLength}`;
}

function validItemOption(item, value) {
  return item.options.some(
    option => value && option.id.toString() === value.toString()
  );
}

function prepareFormFieldValue(item, rawValue, currentValue) {
  if (!item) {
    return rawValue;
  }

  switch (item.element) {
    case FORMELEMENTS_CHECKBOXES:
      return validItemOption(item, rawValue)
        ? toggleBoxesValue(currentValue || [], rawValue)
        : currentValue;

    case FORMELEMENTS_RADIOBUTTONS:
      return validItemOption(item, rawValue) ? [rawValue] : currentValue;

    case FORMELEMENTS_DROPDOWN:
      if (rawValue && !validItemOption(item, rawValue)) {
        return currentValue;
      }

      return rawValue ? [rawValue] : [];
    case FORMELEMENTS_DATEPICKER:
      return prepareDateToFormData(rawValue);
    case FORMELEMENTS_NUMBERINPUT:
    case FORMELEMENTS_TEXTINPUT:
      return rawValue?.replace(/[\r\n\t]/g, '');
    default:
      return rawValue;
  }
}

class FormInput extends React.Component {
  debounceTimer = null;

  state = {
    localValue: undefined
  };

  isDebouncedInput = () => DEBOUNCED_ELEMENTS.has(this.props.item.element);

  getDebounceMs = () =>
    this.props.desktop
      ? FORM_INPUT_DEBOUNCE_MS.desktop
      : FORM_INPUT_DEBOUNCE_MS.mobile;

  valuesEqual = (left, right) => {
    return shallowEqual(left, right);
  };

  getDisplayValue = () =>
    this.state.localValue !== undefined
      ? this.state.localValue
      : this.props.value;

  clearDebounce = () => {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
  };

  flushChange = () => {
    this.clearDebounce();
    if (!this.isDebouncedInput()) return;

    const { item, handleChange, value } = this.props;
    const nextValue = this.getDisplayValue();

    if (this.valuesEqual(nextValue, value)) return;

    handleChange(item, nextValue);
  };

  scheduleChange = value => {
    const { item, handleChange } = this.props;

    this.clearDebounce();
    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = null;
      handleChange(item, value);
    }, this.getDebounceMs());
  };

  shouldComponentUpdate(nextProps, nextState) {
    return (
      !shallowEqual(nextProps, this.props) ||
      !shallowEqual(nextState, this.state)
    );
  }

  componentDidMount() {
    if (
      this.props.item.element === FORMELEMENTS_TEXTAREA &&
      this.props.quickForm
    ) {
      const item = document.getElementById(formWidgetHtmlId(this.props.item));
      item.style.height = 'auto';
      item.style.height = Math.max(100, item.scrollHeight) + 'px';
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.value === this.props.value) return;

    if (this.state.localValue === undefined) return;

    if (this.valuesEqual(this.props.value, this.state.localValue)) {
      this.setState({ localValue: undefined });
      return;
    }

    this.clearDebounce();
  }

  componentWillUnmount() {
    this.flushChange();
  }

  prepareOptions = item => {
    if (item.options && item.options.length) {
      const aoptions = [];
      item.options.forEach(option => {
        option.element = item.element;
        const newStyle = getAbsPlaceStyle(option, this.props.scaleFactor);
        option.style = newStyle;
        if (newStyle.width < 15 || newStyle.height < 15) {
          option.className = 'smallOption';
        }
        aoptions.push(option);
      });
      item.options = aoptions;
    }
    return item;
  };

  isOptionSelected(optionId, value) {
    return value && optionId.toString() === value.toString();
  }

  displayItemAsLabel(item) {
    return item.readOnly && [];
  }

  onChangeHandler = eventOrValue => {
    const { item, handleChange } = this.props;
    let rawValue = eventOrValue;

    if (
      typeof eventOrValue === 'object' &&
      eventOrValue.target &&
      eventOrValue.target.value !== undefined
    ) {
      rawValue = eventOrValue.target.value;
    }

    const nextValue = prepareFormFieldValue(
      item,
      rawValue,
      this.getDisplayValue()
    );

    if (this.valuesEqual(nextValue, this.getDisplayValue())) return;

    this.setState({ localValue: nextValue }, () => {
      if (this.isDebouncedInput()) {
        this.scheduleChange(nextValue);
      } else {
        setTimeout(() => handleChange(item, nextValue), 50);
      }
    });
  };

  onBlurHandler = event => {
    this.flushChange();
    this.props.handleBlur(this.props.item, event);
  };

  onInputClearHandler = event => {
    const { item, handleChange } = this.props;
    const emptyValue = isOptionsWidget(item.element) ? [] : '';

    event.preventDefault();
    this.clearDebounce();
    this.setState({ localValue: emptyValue });

    setTimeout(() => handleChange(item, emptyValue), 50);
  };

  render() {
    const {
      scaleFactor,
      active,
      showActiveAnimation,
      editBarMode,
      absolutePlaceMode,
      showRequiredErrors,
      readOnly,
      item,
      error,
      handleFocus,
      handleClick,
      ios,
      desktop,
      quickForm,
      dpiModifier,
      isQualified,
      hasCustomTabIndex
    } = this.props;

    const readOnlyMode = item.readOnly || readOnly;
    const errorMessage = getFormItemErrorMessage(error, showRequiredErrors);

    const errorClass = errorMessage
      ? ' withError ' +
        (error.apiError && error.updated ? ' withExtApiErrorEdited ' : '')
      : '';
    const animateClass =
      active && showActiveAnimation
        ? errorMessage
          ? ' pulseErr'
          : ' pulse'
        : '';
    const activeClass = active ? ' elementSelected' + animateClass : '';
    const optionalClass = item.optional === true ? ' optional ' : '';
    const readOnlyClass = readOnlyMode ? ' readonly ' : '';
    const Input = FormElements[item.renderAs ? item.renderAs : item.element];
    const currentValue = this.getDisplayValue();
    let displayValue = currentValue;

    switch (item.element) {
      case FORMELEMENTS_DROPDOWN:
        // eslint-disable-next-line no-case-declarations
        const selected = item.options.find(option =>
          this.isOptionSelected(option.id, currentValue)
        );

        displayValue = selected ? selected.label : '';
        break;
      //no default
    }

    const layoutSize = defineLayoutSize(item.layoutSize);
    const absolutePlaceOptions = absolutePlaceMode
      ? getAbsPlaceStyle(item, scaleFactor, displayValue, dpiModifier)
      : {
          //width: defineLayoutWidth(layoutSize)
        };

    const charCountdownInfo = getCharCountdownInfo(
      item,
      currentValue,
      readOnlyMode
    );
    const maxLength = resolveMaxLength(item.maxLength);
    const recipientHint = resolveRecipientHint(item);
    const isTextLike = TEXT_LIKE_ELEMENTS.has(item.element);
    const isEmpty = isEmptyFormValue(currentValue);
    const showHintAsPlaceholder = Boolean(
      isTextLike && isEmpty && recipientHint
    );
    const showHintAsTooltip = Boolean(
      recipientHint && (!isTextLike || !isEmpty) && !absolutePlaceMode
    );

    let tooltip;
    let tooltipError = false;
    if (absolutePlaceMode) {
      // Boxes attach per-option tooltips in FormElements (legacy PDF logic).
      if (!isBoxesWidget(item.element)) {
        const tooltipInfo = getAbsolutePlaceTooltipInfo(
          item,
          null,
          errorMessage
        );
        tooltip = tooltipInfo.text || undefined;
        tooltipError = tooltipInfo.isError;
      }
    } else if (showHintAsTooltip) {
      tooltip = recipientHint;
    }

    return Input({
      onChange: this.onChangeHandler,
      onFocus: event => handleFocus(item, event),
      onBlur: event => this.onBlurHandler(event),
      onClick: event => !readOnlyMode && handleClick(item, event),
      onInputClear: this.onInputClearHandler,
      showPopupsInPortal: this.props.showPopupsInPortal,
      scale: scaleFactor,
      radioselect: item.radioselect,
      editBarMode: editBarMode,
      title: item.label,
      tooltip,
      tooltipError,
      textStyle: item.textStyle,
      key: `${item.liveKey}`,
      inputId: formWidgetHtmlId(item),
      inputWidth: absolutePlaceOptions.width,
      ios: ios,
      data: absolutePlaceMode ? this.prepareOptions(item) : item,
      requestedDocuments: this.props.requestedDocuments,
      absolutePlaceMode: absolutePlaceMode,
      animateClass: animateClass,
      className:
        item.liveKey +
        ' ' +
        md5Password(item.label || '') +
        ' ' +
        item.element +
        ' inputElement ' +
        optionalClass +
        readOnlyClass +
        activeClass +
        errorClass +
        (item.hidden ? ' hidden ' : '') +
        (quickForm ? ' fade-in ' : '') +
        (charCountdownInfo ? ' hasCharCountdown ' : '') +
        `ls-${layoutSize}`,
      readonly: readOnlyMode,
      value: currentValue,
      label: (!quickForm && item.tooltip) || item.label,
      placeholder:
        !absolutePlaceMode && showHintAsPlaceholder ? recipientHint : '',
      name: formWidgetHtmlId(item),
      optionalInfo: item.optional === true && !readOnlyMode ? 'optional' : '',
      desktop: desktop,
      outline: quickForm,
      quickForm: quickForm,
      errorMessage: errorMessage,
      errorMessageForce: true,
      disableFloatingLabel: absolutePlaceMode,
      style: absolutePlaceOptions,
      maxlength: maxLength || 9999,
      info: charCountdownInfo || undefined,
      monoSpace: item.monoSpace,
      optionsLayoutSize: item.optionsLayoutSize,
      tabindex:
        item.tabIndex !== undefined
          ? item.tabIndex
          : hasCustomTabIndex
          ? 9999
          : 0,
      renderAsInput: Boolean(item.renderAs),
      activeMode: active || editBarMode,
      isQualified: isQualified
    });
  }
}

FormInput.propTypes = {
  showPopupsInPortal: PropTypes.bool,
  scaleFactor: PropTypes.object,
  active: PropTypes.bool,
  showActiveAnimation: PropTypes.bool,
  editBarMode: PropTypes.any,
  absolutePlaceMode: PropTypes.bool,
  error: PropTypes.object,
  showRequiredErrors: PropTypes.bool,
  item: PropTypes.object,
  readOnly: PropTypes.bool,
  ios: PropTypes.bool,
  handleChange: PropTypes.func,
  handleBlur: PropTypes.func,
  handleClick: PropTypes.func,
  handleFocus: PropTypes.func,
  value: PropTypes.any,
  defaultValue: PropTypes.any,
  desktop: PropTypes.bool,
  quickForm: PropTypes.bool,
  isQualified: PropTypes.bool,
  isFieldLinked: PropTypes.bool,
  requestedDocuments: PropTypes.array,
  hasCustomTabIndex: PropTypes.bool,
  dpiModifier: PropTypes.number
};

export default FormInput;
